import he from 'he'
import { extractPlainText, RichTextContent, RolodexEntryType } from 'types'

/**
 * Interface representing an extracted entry from RichTextContent
 */
export interface ExtractedEntry {
  entryType: RolodexEntryType
  entryId: string
  entryName?: string
  metadata?: Record<string, unknown>
}

/**
 * Parses a RichTextContent string and extracts all entry IDs and types from SlashCommandNode instances.
 *
 * @param content - The RichTextContent string to parse
 * @returns Array of objects containing entry type, ID, name, and metadata
 */
export function extractEntriesFromRichText(
  content: RichTextContent
): ExtractedEntry[] {
  const entries: ExtractedEntry[] = []

  if (!content || typeof content !== 'string') {
    return entries
  }

  const slashCommandSpanRegex =
    /<span[^>]*data-lexical-slash-command=["']true["'][^>]*>/g

  let match
  while ((match = slashCommandSpanRegex.exec(content)) !== null) {
    const spanTag = match[0]

    const entryTypeMatch = spanTag.match(/data-entry-type=["']([^"']+)["']/)
    const entryType = entryTypeMatch?.[1] as RolodexEntryType

    const entryIdMatch = spanTag.match(/data-entry-id=["']([^"']+)["']/)
    const entryId = entryIdMatch?.[1]

    const entryNameMatch = spanTag.match(/data-entry-name=["']([^"']+)["']/)
    const entryName = entryNameMatch?.[1]

    const metadataMatch = spanTag.match(/data-metadata=(["'])([^]*?)\1/)
    let metadata: Record<string, unknown> | undefined
    if (metadataMatch?.[2]) {
      try {
        // The metadata is JSON-encoded, so we need to decode HTML entities and parse
        const metadataStr = he.decode(metadataMatch[2])
        metadata = JSON.parse(metadataStr)
      } catch {
        metadata = undefined
      }
    }

    if (entryType && entryId) {
      entries.push({
        entryType,
        entryId,
        entryName,
        metadata,
      })
    }
  }

  return entries
}

/**
 * Extracts unique entry IDs from RichTextContent, regardless of entry type.
 *
 * @param content - The RichTextContent string to parse
 * @returns Array of unique entry IDs
 */
export function extractUniqueEntryIds(content: RichTextContent): string[] {
  const entries = extractEntriesFromRichText(content)
  const uniqueIds = new Set(entries.map((entry) => entry.entryId))
  return Array.from(uniqueIds)
}

/**
 * Extracts entries grouped by their type from RichTextContent.
 *
 * @param content - The RichTextContent string to parse
 * @returns Object mapping entry types to arrays of entry IDs
 */
export function extractEntriesByType(
  content: RichTextContent
): Record<RolodexEntryType, string[]> {
  const entries = extractEntriesFromRichText(content)
  const entriesByType: Record<string, string[]> = {}

  entries.forEach((entry) => {
    if (!entriesByType[entry.entryType]) {
      entriesByType[entry.entryType] = []
    }
    if (!entriesByType[entry.entryType].includes(entry.entryId)) {
      entriesByType[entry.entryType].push(entry.entryId)
    }
  })

  return entriesByType as Record<RolodexEntryType, string[]>
}

/**
 * Extracts unique user IDs from @ mentions in RichTextContent.
 * Parses the Lexical mention format (data-lexical-mention).
 *
 * @param content - The RichTextContent string to parse
 * @returns Array of unique user IDs that were mentioned
 */
export function extractMentionedUserIds(content: RichTextContent): string[] {
  if (!content || typeof content !== 'string') {
    return []
  }

  const lexicalMentionRegex =
    /<span[^>]*data-lexical-mention=["']true["'][^>]*data-user-id=["']([^"']+)["'][^>]*>/g

  const userIds = new Set(
    Array.from(
      content.matchAll(lexicalMentionRegex),
      (match) => match[1]
    ).filter(Boolean)
  )

  return Array.from(userIds)
}

/**
 * Extracts context (one sentence before and after) around a mention for a specific user.
 *
 * @param content - The RichTextContent string to parse
 * @param userId - The user ID to find context for
 * @returns The extracted context text, or undefined if mention not found
 */
export function extractMentionContext(
  content: RichTextContent,
  userId: string
): string | undefined {
  if (!content || typeof content !== 'string') {
    return undefined
  }

  const plainText = extractPlainText(content)
  const mentionInfo = findMentionInContent(content, userId)
  if (!mentionInfo) return undefined

  const mentionIndex = findMentionInPlainText(plainText, mentionInfo.userName)
  if (mentionIndex === -1) return undefined

  return extractContextAroundMention(plainText, mentionIndex)
}

interface MentionInfo {
  userName: string
}

/**
 * Finds mention information in HTML content for a specific user ID.
 */
function findMentionInContent(
  content: string,
  userId: string
): MentionInfo | undefined {
  const mentionPattern =
    /<span[^>]*data-lexical-mention=["']true["'][^>]*data-user-id=["']([^"']+)["'][^>]*>/g

  for (const match of content.matchAll(mentionPattern)) {
    if (match[1] === userId) {
      const fullTag = match[0]
      const userNameMatch = fullTag.match(/data-user-name=["']([^"']+)["']/)
      const closingTag = '</span>'
      const tagEnd = content.indexOf(closingTag, match.index)

      if (tagEnd !== -1) {
        const textContent = content
          .substring(match.index + fullTag.length, tagEnd)
          .trim()
          .replace(/^@/, '')

        return {
          userName: textContent || userNameMatch?.[1] || '',
        }
      }
    }
  }

  return undefined
}

/**
 * Finds the index of a mention in plain text.
 */
function findMentionInPlainText(plainText: string, userName: string): number {
  if (!userName) return -1

  const escaped = userName.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')
  const patterns = [
    new RegExp(`@${escaped}\\b`, 'gi'),
    new RegExp(`\\b${escaped}\\b`, 'gi'),
  ]

  for (const pattern of patterns) {
    const match = plainText.match(pattern)
    if (match?.index !== undefined) return match.index
  }

  return -1
}

/**
 * Extracts context (one sentence before and after) around a mention index.
 * Falls back to fixed-length context if sentence boundaries can't be found.
 */
function extractContextAroundMention(
  plainText: string,
  mentionIndex: number
): string {
  const sentences = splitIntoSentences(plainText)
  const mentionSentenceIndex = sentences.findIndex(
    (s) => mentionIndex >= s.start && mentionIndex <= s.end
  )

  if (mentionSentenceIndex === -1) {
    // Fallback: extract fixed-length context
    const contextLength = 150
    const start = Math.max(0, mentionIndex - contextLength)
    const end = Math.min(plainText.length, mentionIndex + contextLength)
    return plainText.substring(start, end).trim()
  }

  const startIndex = Math.max(0, mentionSentenceIndex - 1)
  const endIndex = Math.min(sentences.length - 1, mentionSentenceIndex + 1)

  return plainText
    .substring(sentences[startIndex].start, sentences[endIndex].end)
    .trim()
}

/**
 * Splits text into sentences based on punctuation.
 */
function splitIntoSentences(
  text: string
): Array<{ start: number; end: number }> {
  const sentences: Array<{ start: number; end: number }> = []
  const sentenceEndRegex = /[.!?]\s+/g
  let lastIndex = 0

  for (const match of text.matchAll(sentenceEndRegex)) {
    sentences.push({
      start: lastIndex,
      end: match.index + 1,
    })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    sentences.push({
      start: lastIndex,
      end: text.length,
    })
  }

  return sentences
}
